import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { type DocumentSummary, RequestError, api } from '../../lib/api'

const STATUS_FA: Record<string, string> = {
  uploaded: 'در صف',
  extracting: 'در حال خواندن',
  extracted: 'خوانده شد',
  verifying: 'در حال بررسی',
  verified: 'بررسی شد',
  failed: 'خوانده نشد',
}

const VERDICT_FA: Record<string, string> = {
  supports: 'تأیید می‌کند',
  contradicts: 'نقض می‌کند',
  silent: 'چیزی نمی‌گوید',
}

export function DocumentsPage() {
  const queryClient = useQueryClient()
  const fileInput = useRef<HTMLInputElement>(null)
  const [provenance, setProvenance] = useState<'self_authored' | 'independent'>('self_authored')
  const [open, setOpen] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: api.documents,
    /* Extraction and verification run after the upload responds, so the list
       keeps looking until the work lands.
       Polls while the data is still unknown, not only while it is known to be
       working: on the first render there is no data yet, and treating that as
       "nothing in progress" stops the interval before it ever starts — which
       leaves a verified document sitting on screen as «در حال بررسی» until
       someone reloads. */
    refetchInterval: (query) => {
      const documents = (query.state.data as { documents: DocumentSummary[] } | undefined)?.documents
      if (!documents) return 3000
      const working = documents.some((d) =>
        ['uploaded', 'extracting', 'verifying'].includes(d.status),
      )
      return working ? 3000 : false
    },
  })

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadDocument(file, provenance),
    onSuccess: () => {
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (err) => setError(err instanceof RequestError ? err.messageFa : 'بارگذاری انجام نشد.'),
  })

  const reverify = useMutation({
    mutationFn: (id: number) => api.reverifyDocument(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['documents'] }),
  })

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteDocument(id),
    onSuccess: () => {
      setOpen(null)
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
      void queryClient.invalidateQueries({ queryKey: ['ledger'] })
    },
  })

  return (
    <div className="container stack">
      <div className="card">
        <h1>اسناد</h1>
        <p className="meta">
          گفت‌وگو تا جایی می‌تواند پیش برود که «خودمان گفته‌ایم». سند است که یک گزاره را از حرف به
          شواهد تبدیل می‌کند — و فقط گزاره‌های مستند به کار گواهی می‌آیند. هر چیزی که از سند برداشت
          کنم، همراه با همان جمله و شماره صفحه‌اش نشان می‌دهم تا خودتان ببینید چه چیزی مرا قانع کرده.
        </p>

        <div className="field">
          <label htmlFor="provenance">این سند را چه کسی تهیه کرده؟</label>
          <div className="options">
            <button
              type="button"
              className={`option ${provenance === 'self_authored' ? 'is-selected' : ''}`}
              onClick={() => setProvenance('self_authored')}
            >
              <span>خود مؤسسه آن را نوشته است</span>
            </button>
            <button
              type="button"
              className={`option ${provenance === 'independent' ? 'is-selected' : ''}`}
              onClick={() => setProvenance('independent')}
            >
              <span>از یک مرجع بیرونی است (ثبت، حسابرس، نهاد صادرکننده مجوز)</span>
            </button>
          </div>
          <p className="meta" style={{ marginTop: 'var(--space-2)' }}>
            سند مرجع بیرونی وزن بیشتری دارد، چون نوشته خود مؤسسه درباره خودش نیست.
          </p>
        </div>

        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.docx,.txt,.md"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) upload.mutate(file)
            e.target.value = ''
          }}
        />
        <button
          type="button"
          className="btn"
          disabled={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {upload.isPending ? 'در حال بارگذاری…' : 'بارگذاری سند'}
        </button>
        <p className="meta" style={{ marginTop: 'var(--space-2)' }}>
          PDF، Word یا متنی. حداکثر ۲۵ مگابایت.
        </p>

        {error && <div className="notice notice--error" style={{ marginTop: 'var(--space-3)' }}>{error}</div>}
      </div>

      <div className="card">
        {isLoading && <p className="muted">در حال بارگذاری…</p>}
        {!isLoading && (data?.documents.length ?? 0) === 0 && (
          <p className="meta">هنوز سندی بارگذاری نشده است.</p>
        )}

        {(data?.documents ?? []).map((document) => (
          <div key={document.id} className="dimension" style={{ gridTemplateColumns: '1fr auto' }}>
            <div>
              <div className="dimension__title">{document.filename}</div>
              <div className="meta">
                {STATUS_FA[document.status] ?? document.status}
                {document.detectedType && ` · نوع: ${document.detectedType}`}
                {document.pageCount !== null && (
                  <> · <span className="num">{document.pageCount}</span> صفحه</>
                )}
              </div>
              {document.statusDetail && (
                <div className="notice notice--error" style={{ marginTop: 'var(--space-2)' }}>
                  {document.statusDetail}
                </div>
              )}
            </div>
            <div className="row">
              {document.status === 'verified' && (
                <button
                  type="button"
                  className="btn btn--quiet btn--sm"
                  onClick={() => setOpen(open === document.id ? null : document.id)}
                >
                  {open === document.id ? 'بستن' : 'چه چیزی را ثابت کرد'}
                </button>
              )}
              {/* These failures are almost always transient — a timeout, a proxy,
                  a rate limit — and the alternative is a dead end. */}
              {(document.status === 'failed' || document.status === 'extracted') && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={reverify.isPending}
                  onClick={() => reverify.mutate(document.id)}
                >
                  تلاش دوباره
                </button>
              )}
              <button
                type="button"
                className="btn btn--quiet btn--sm"
                onClick={() => remove.mutate(document.id)}
              >
                حذف
              </button>
            </div>
            {open === document.id && <Claims documentId={document.id} />}
          </div>
        ))}
      </div>
    </div>
  )
}

function Claims({ documentId }: { documentId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['document-claims', documentId],
    queryFn: () => api.documentClaims(documentId),
  })

  if (isLoading || !data) {
    return <p className="meta" style={{ gridColumn: '1 / -1' }}>…</p>
  }

  const speaking = data.claims.filter((c) => c.verdict !== 'silent')

  return (
    <div style={{ gridColumn: '1 / -1', marginTop: 'var(--space-3)' }}>
      {speaking.length === 0 && (
        <p className="meta">
          این سند درباره هیچ‌کدام از گزاره‌های استاندارد چیز مشخصی نگفت. این یعنی چیزی پیدا نشد، نه
          اینکه مؤسسه در چیزی ناموفق بوده.
        </p>
      )}
      {speaking.map((claim) => (
        <div
          key={`${claim.statementCode}-${claim.levelNo}`}
          style={{ padding: 'var(--space-3) 0', borderTop: '1px solid var(--line)' }}
        >
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="meta">گزاره {claim.statementCode}</span>
            <span className={`badge ${claim.verdict === 'supports' ? 'badge--good' : 'badge--bad'}`}>
              {VERDICT_FA[claim.verdict]}
              {claim.score !== null && (
                <> · <span className="num">{claim.score}</span> از <span className="num">۳</span></>
              )}
            </span>
          </div>
          <div style={{ lineHeight: 1.9 }}>{claim.statementFa}</div>
          {/* The quote is the whole point. Nobody signs these off, so the reader
              has to be able to see exactly what convinced the system. */}
          {claim.quoteFa && (
            <blockquote className="statement-ref" style={{ marginTop: 'var(--space-2)' }}>
              «{claim.quoteFa}»
              {claim.pageNo !== null && (
                <span className="meta"> — صفحه <span className="num">{claim.pageNo}</span></span>
              )}
            </blockquote>
          )}
        </div>
      ))}
    </div>
  )
}
